// Company store editor — the collection (S2, CEO 2026-10-12). The rep starts
// from a starter collection and/or adds products through the shared
// AddProductModal, limits each product's colours (none ticked = every colour)
// and puts them in order. The list is a local draft: the editor saves it with
// PUT /company-stores/:id/products, in the order shown.
//
// Decoration: a product already in the store keeps what is saved; a starter
// pick carries the starter's branding; a product added by hand is sent with
// no decoration so the API gives it the website's usual one
// (defaultStoreDecoration). Product names are the website's (store-facing)
// names, never the supplier's.
//
// Mock-ups (CEO 2026-10-19): employees see a product's approved virtual of
// the store's saved logo instead of the plain photo. Each saved product says
// whether it has one, and "Make a mock-up" opens Quick virtual (the existing
// virtuals flow, in a new tab) with this client, product, colours and the
// store's logo filled in. Nothing is rendered until the rep clicks Make there
// (renders count against the monthly cap), and only approved ones show.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowDown, ArrowUp, ExternalLink, Layers, Loader2, Plus, Trash2 } from "lucide-react";
import { AddProductModal, type AddProductRow } from "@/components/catalog/AddProductModal";
import { Button } from "@/components/ui/Button";
import { api, apiErrorMessage } from "@/lib/api";
import { brandingSummary, money, type StarterCollection, type StoreDecoration, type StoreProduct } from "@/lib/companyStores";
import { cn } from "@/lib/utils";

/** The API's per-store cap (productsBody max 60). */
export const MAX_STORE_PRODUCTS = 60;

export interface DraftProduct {
  productId: string;
  name: string | null;
  photo: string | null;
  sku: string | null;
  /** undefined = added by hand, not saved yet (the API picks the branding). */
  decoration: StoreDecoration | undefined;
  colors: string[];
  active: boolean;
  /** undefined = not saved yet, so the website price isn't known here. */
  headline: StoreProduct["headline"] | undefined;
  /** The listing has left the website: it must be removed before saving. */
  gone: boolean;
  /** undefined = not saved yet. Approved virtuals of the saved store logo. */
  mockups?: StoreProduct["mockups"];
}

/** Who and what a mock-up is for: the store's client and its SAVED logo. */
export interface MockupTarget {
  clientId: string;
  clientName: string;
  logoFileId: string;
}

/** Quick virtual, filled in for one store product (the existing virtuals flow). */
export function quickVirtualLink(t: MockupTarget, p: Pick<DraftProduct, "productId" | "colors">): string {
  const q = new URLSearchParams({ client: t.clientId, clientName: t.clientName, listing: p.productId, logo: t.logoFileId });
  if (p.colors.length) q.set("colours", p.colors.join(","));
  return `/virtuals?${q.toString()}`;
}

export function draftFromStoreProduct(p: StoreProduct): DraftProduct {
  return {
    productId: p.productId,
    name: p.name,
    photo: p.photo,
    sku: p.sku,
    decoration: p.decoration,
    colors: p.colors,
    active: p.active,
    headline: p.headline,
    gone: p.name === null,
    mockups: p.mockups ?? [],
  };
}

/** The PUT body for one product. */
export function productPayload(p: DraftProduct) {
  return {
    productId: p.productId,
    ...(p.decoration === undefined ? {} : { decoration: p.decoration }),
    colors: p.colors,
    active: p.active,
  };
}

interface Colorway {
  name: string;
  images: Array<{ src: string; label: string }>;
}

export function StoreProductsSection(props: {
  products: DraftProduct[];
  onChange: (next: DraftProduct[]) => void;
  disabled: boolean;
  /** Null = a new store or no saved logo yet (no mock-ups to make). */
  mockupTarget?: MockupTarget | null;
}) {
  const { products, onChange, disabled, mockupTarget = null } = props;
  const [startersOpen, setStartersOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [openColours, setOpenColours] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const full = products.length >= MAX_STORE_PRODUCTS;

  const has = (id: string) => products.some((p) => p.productId === id);

  const addStarter = (s: StarterCollection) => {
    const fresh = s.products.filter((p) => !has(p.productId));
    const room = MAX_STORE_PRODUCTS - products.length;
    const take = fresh.slice(0, Math.max(0, room));
    onChange([
      ...products,
      ...take.map((p) => ({
        productId: p.productId,
        name: p.name,
        photo: p.photo,
        sku: null,
        decoration: p.decoration,
        colors: [],
        active: true,
        headline: undefined,
        gone: false,
      })),
    ]);
    const skipped = s.products.length - take.length;
    setNote(
      take.length === 0
        ? `Everything in ${s.name} is already in the store.`
        : `Added ${take.length} product${take.length === 1 ? "" : "s"} from ${s.name}${skipped ? ` (${skipped} already in the store or over the limit)` : ""}. Save to keep them.`,
    );
    setStartersOpen(false);
  };

  const addRow = async (row: AddProductRow) => {
    if (has(row.id)) throw new Error("That product is already in this store.");
    if (products.length >= MAX_STORE_PRODUCTS) throw new Error(`A store can hold up to ${MAX_STORE_PRODUCTS} products.`);
    onChange([
      ...products,
      { productId: row.id, name: row.name, photo: row.primaryImageUrl, sku: row.sku ?? null, decoration: undefined, colors: [], active: true, headline: undefined, gone: false },
    ]);
  };

  const move = (i: number, by: -1 | 1) => {
    const j = i + by;
    if (j < 0 || j >= products.length) return;
    const next = [...products];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  const remove = (i: number) => onChange(products.filter((_, k) => k !== i));
  const setColours = (i: number, colors: string[]) => onChange(products.map((p, k) => (k === i ? { ...p, colors } : p)));

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setStartersOpen((o) => !o)} disabled={disabled || full} aria-expanded={startersOpen}>
          <Layers className="h-4 w-4" aria-hidden /> Start from a collection
        </Button>
        <Button variant="outline" onClick={() => setAdding(true)} disabled={disabled || full}>
          <Plus className="h-4 w-4" aria-hidden /> Add product
        </Button>
      </div>
      {full ? <p className="mt-2 text-[13px] text-muted">A store can hold up to {MAX_STORE_PRODUCTS} products.</p> : null}
      {note ? <p role="status" className="mt-2 text-[13px] text-ink">{note}</p> : null}

      {startersOpen ? <StarterPicker onPick={addStarter} has={has} /> : null}

      {products.length === 0 ? (
        <p className="mt-4 rounded-md border border-dashed border-rule px-4 py-6 text-center text-sm text-muted">
          No products yet. Start from a collection, then add any extras.
        </p>
      ) : (
        <ol className="mt-4 divide-y divide-rule rounded-md border border-rule bg-white" aria-label="Products in this store">
          {products.map((p, i) => (
            <li key={p.productId} className="p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded border border-rule bg-canvas">
                  {p.photo ? <img src={p.photo} alt="" className="max-h-full max-w-full object-contain" loading="lazy" /> : null}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-ink">{p.name ?? "Product no longer on the website"}</p>
                  {p.gone ? (
                    <p className="mt-0.5 flex items-center gap-1 text-[13px] text-risk">
                      <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> It can't be ordered. Remove it to save the store.
                    </p>
                  ) : (
                    <>
                      <p className="mt-0.5 text-[13px] text-muted">
                        {brandingSummary(p.decoration)}
                        {p.sku ? <span> · {p.sku}</span> : null}
                      </p>
                      {p.headline === null ? (
                        <p className="mt-0.5 text-[13px] text-warn">No website price yet, so people can't order it until it's priced.</p>
                      ) : p.headline ? (
                        <p className="mt-0.5 text-[13px] text-muted">From {money(p.headline.each)} each at {p.headline.at}</p>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setOpenColours((o) => (o === p.productId ? null : p.productId))}
                        aria-expanded={openColours === p.productId}
                        className="mt-1 text-[13px] text-ink underline underline-offset-4 hover:no-underline"
                      >
                        {p.colors.length === 0 ? "All colors" : `${p.colors.length} color${p.colors.length === 1 ? "" : "s"}: ${p.colors.join(", ")}`}
                        {openColours === p.productId ? " · close" : " · change"}
                      </button>
                      <MockupLine product={p} target={mockupTarget} />
                    </>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(i, -1)} disabled={disabled || i === 0} aria-label={`Move ${p.name ?? "product"} up`}>
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(i, 1)} disabled={disabled || i === products.length - 1} aria-label={`Move ${p.name ?? "product"} down`}>
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(i)} disabled={disabled} aria-label={`Remove ${p.name ?? "product"}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {openColours === p.productId && !p.gone ? (
                <ColourPicker productId={p.productId} selected={p.colors} onChange={(c) => setColours(i, c)} disabled={disabled} />
              ) : null}
            </li>
          ))}
        </ol>
      )}

      {adding ? (
        <AddProductModal
          onClose={() => setAdding(false)}
          onAdd={(row) => addRow(row)}
          existingProductIds={new Set(products.map((p) => p.productId))}
        />
      ) : null}
    </div>
  );
}

/** Does this product show a mock-up to employees — and a way to make one. */
function MockupLine({ product, target }: { product: DraftProduct; target: MockupTarget | null }) {
  if (!target) return <p className="mt-1 text-[13px] text-muted">Mock-ups: save the store with its logo first.</p>;
  if (product.mockups === undefined) return <p className="mt-1 text-[13px] text-muted">Mock-ups: save the store first.</p>;
  const n = product.mockups.length;
  const make = (
    <a
      href={quickVirtualLink(target, product)}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-ink underline underline-offset-4 hover:no-underline"
    >
      {n ? "Make more" : "Make a mock-up"} <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[13px]">
      {n ? (
        <>
          {product.mockups.slice(0, 4).map((m) => (
            <img key={m.url} src={m.url} alt={`Mock-up${m.colour ? ` in ${m.colour}` : ""}`} title={m.colour ?? undefined} className="h-9 w-9 rounded border border-rule bg-canvas object-contain" loading="lazy" />
          ))}
          <span className="text-muted">
            Employees see the mock-up ({n} color{n === 1 ? "" : "s"}{product.mockups.some((m) => m.colour) ? `: ${product.mockups.map((m) => m.colour).filter(Boolean).join(", ")}` : ""}).
          </span>
        </>
      ) : (
        <span className="text-warn">No mock-up yet, so employees see the plain photo.</span>
      )}
      {make}
      <span className="w-full text-[12px] text-muted">Opens Quick virtual with this product and the store's logo. Nothing is made until you click Make there; approve it and it shows here.</span>
    </div>
  );
}

function StarterPicker(props: { onPick: (s: StarterCollection) => void; has: (id: string) => boolean }) {
  const q = useQuery({
    queryKey: ["company-stores", "starters"],
    queryFn: () => api<{ starters: StarterCollection[] }>("/company-stores/starters"),
    staleTime: 5 * 60 * 1000,
  });
  if (q.isLoading) {
    return (
      <p className="mt-3 flex items-center gap-2 text-sm text-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading collections…
      </p>
    );
  }
  if (q.isError) return <p role="alert" className="mt-3 text-sm text-risk">{apiErrorMessage(q.error, "We couldn't load the collections.")}</p>;
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      {(q.data?.starters ?? []).map((s) => {
        const fresh = s.products.filter((p) => !props.has(p.productId)).length;
        return (
          <div key={s.slug} className="flex flex-col rounded-md border border-rule bg-white p-4">
            <p className="font-semibold text-ink">{s.name}</p>
            <p className="mt-0.5 text-[13px] text-muted">{s.blurb}</p>
            <div className="mt-3 flex flex-wrap gap-1.5" aria-hidden>
              {s.products.slice(0, 8).map((p) => (
                <span key={p.productId} className="flex h-10 w-10 items-center justify-center overflow-hidden rounded border border-rule bg-canvas" title={p.name}>
                  {p.photo ? <img src={p.photo} alt="" className="max-h-full max-w-full object-contain" loading="lazy" /> : null}
                </span>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-3 self-start" onClick={() => props.onPick(s)} disabled={fresh === 0}>
              {fresh === 0 ? "Already added" : `Add ${fresh} product${fresh === 1 ? "" : "s"}`}
            </Button>
          </div>
        );
      })}
    </div>
  );
}

function ColourPicker(props: { productId: string; selected: string[]; onChange: (c: string[]) => void; disabled: boolean }) {
  const q = useQuery({
    queryKey: ["storefront", "colorways", props.productId],
    queryFn: () => api<{ colorways: Colorway[] }>(`/storefront/products/${props.productId}/colorways`),
    staleTime: 5 * 60 * 1000,
  });
  const ways = q.data?.colorways ?? [];
  const names = new Set(ways.map((w) => w.name));
  const stale = q.data ? props.selected.filter((c) => !names.has(c)) : [];
  const toggle = (name: string) =>
    props.onChange(props.selected.includes(name) ? props.selected.filter((c) => c !== name) : [...props.selected, name]);

  return (
    <fieldset className="mt-3 rounded-md border border-rule bg-canvas p-3" disabled={props.disabled}>
      <legend className="sr-only">Colors people can pick</legend>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-muted">Tick the colors people can pick. Leave all unticked to offer every color.</p>
        {props.selected.length ? (
          <button type="button" onClick={() => props.onChange([])} className="text-[13px] text-ink underline underline-offset-4 hover:no-underline">
            Offer every color
          </button>
        ) : null}
      </div>
      {q.isLoading ? (
        <p className="flex items-center gap-2 text-[13px] text-muted"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading colors…</p>
      ) : q.isError ? (
        <p role="alert" className="text-[13px] text-risk">{apiErrorMessage(q.error, "We couldn't load this product's colors.")}</p>
      ) : ways.length === 0 ? (
        <p className="text-[13px] text-muted">This product has no color choices.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {ways.map((w) => {
            const on = props.selected.includes(w.name);
            return (
              <label
                key={w.name}
                className={cn(
                  "flex min-h-[36px] cursor-pointer items-center gap-2 rounded-md border bg-white px-2 py-1 text-[13px]",
                  on ? "border-ink text-ink" : "border-rule text-muted hover:text-ink",
                )}
              >
                <input type="checkbox" className="h-4 w-4 accent-ink" checked={on} onChange={() => toggle(w.name)} />
                {w.images[0] ? <img src={w.images[0].src} alt="" className="h-6 w-6 rounded object-cover" loading="lazy" /> : null}
                {w.name}
              </label>
            );
          })}
        </div>
      )}
      {stale.length ? (
        <p className="mt-2 text-[13px] text-warn">
          No longer offered: {stale.join(", ")}.{" "}
          <button type="button" className="underline underline-offset-4" onClick={() => props.onChange(props.selected.filter((c) => names.has(c)))}>
            Remove {stale.length === 1 ? "it" : "them"}
          </button>
        </p>
      ) : null}
    </fieldset>
  );
}
